import {
	useMutation,
	useQuery,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import type {
	MetaAdsApproveDto,
	MetaAdsRejectDto,
	MetaAdsSetApproversDto,
	MetaAdsSubmitForApprovalDto,
} from "shared";
import { Api } from "../api.config";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { parseError } from "../common/error";
import { useCompany } from "../company/useCompany";
import { QueryKeys } from "../LinkGen";

export const useApprovalSettings = () => {
	const company = useCompany();
	return useSuspenseQuery({
		queryKey: QueryKeys.Integrations.MetaAds.ApprovalSettings(company.id),
		queryFn: () => Api.GetMetaAdsApprovalSettings().then((r) => r.data),
	});
};

export const useApprovals = (status?: string) => {
	const company = useCompany();
	return useSuspenseQuery({
		queryKey: QueryKeys.Integrations.MetaAds.Approvals(company.id, status),
		queryFn: () => Api.GetMetaAdsApprovals(status).then((r) => r.data),
	});
};

export const useApproval = (id: number) => {
	const company = useCompany();
	return useSuspenseQuery({
		queryKey: QueryKeys.Integrations.MetaAds.Approval(company.id, id),
		queryFn: () => Api.GetMetaAdsApproval(id).then((r) => r.data),
	});
};

export const usePendingApprovalCount = () => {
	const company = useCompany();
	return useQuery({
		queryKey: QueryKeys.Integrations.MetaAds.PendingApprovalCount(company.id),
		queryFn: () =>
			Api.GetMetaAdsPendingApprovalCount().then((r) => r.data.count),
		refetchInterval: 60_000,
	});
};

export const useApprovalActions = () => {
	const company = useCompany();
	const queryClient = useQueryClient();
	const emit = useAlertEmit();

	const approvalsQueryPrefix = [
		"COMPANY",
		company.id,
		"INTEGRATIONS",
		"META_ADS",
		"APPROVALS",
	] as const;

	const invalidateApprovals = async (approvalId?: number) => {
		await queryClient.invalidateQueries({
			queryKey: [...approvalsQueryPrefix],
		});
		if (approvalId != null) {
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.MetaAds.Approval(
					company.id,
					approvalId,
				),
			});
		}
		queryClient.invalidateQueries({
			queryKey: QueryKeys.Integrations.MetaAds.PendingApprovalCount(company.id),
		});
		queryClient.invalidateQueries({
			queryKey: QueryKeys.Integrations.MetaAds.Drafts(company.id),
		});
	};

	const submitForApproval = useMutation({
		mutationFn: (dto: MetaAdsSubmitForApprovalDto) =>
			Api.SubmitMetaAdsCampaignForApproval(dto).then((r) => r.data),
		onSuccess: async () => {
			await invalidateApprovals();
			emit({
				type: AlertType.SUCCESS,
				content: "Campaign submitted for approval.",
			});
		},
		onError: (error) => {
			emit({
				type: AlertType.ERROR,
				content: parseError(error) ?? "Failed to submit campaign for approval.",
			});
		},
	});

	const approve = useMutation({
		mutationFn: ({ id, dto }: { id: number; dto: MetaAdsApproveDto }) =>
			Api.ApproveMetaAdsCampaign(id, dto).then((r) => r.data),
		onSuccess: async (_data, { id }) => {
			await invalidateApprovals(id);
			emit({
				type: AlertType.SUCCESS,
				content: "Campaign approved and published.",
			});
		},
		onError: (error) => {
			emit({
				type: AlertType.ERROR,
				content: parseError(error) ?? "Failed to approve campaign.",
			});
		},
	});

	const reject = useMutation({
		mutationFn: ({ id, dto }: { id: number; dto: MetaAdsRejectDto }) =>
			Api.RejectMetaAdsCampaign(id, dto).then((r) => r.data),
		onSuccess: async (_data, { id }) => {
			await invalidateApprovals(id);
			emit({
				type: AlertType.WARNING,
				content: "Campaign rejected.",
			});
		},
		onError: (error) => {
			emit({
				type: AlertType.ERROR,
				content: parseError(error) ?? "Failed to reject campaign.",
			});
		},
	});

	const updateApprovalEnabled = useMutation({
		mutationFn: (enabled: boolean) => Api.UpdateMetaAdsApprovalEnabled(enabled),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.MetaAds.ApprovalSettings(company.id),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Approval settings updated.",
			});
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to update approval settings.",
			});
		},
	});

	const setApprovers = useMutation({
		mutationFn: (dto: MetaAdsSetApproversDto) => Api.SetMetaAdsApprovers(dto),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.MetaAds.ApprovalSettings(company.id),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Approvers updated.",
			});
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to update approvers.",
			});
		},
	});

	return {
		submitForApproval,
		approve,
		reject,
		updateApprovalEnabled,
		setApprovers,
	};
};
